'use client';

import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { HeartIcon, RulerIcon } from '@/components/aarohi/Icons';
import SizeChart from './SizeChart';

interface Props {
  product: {
    id: number;
    slug: string;
    name: string;
    price: number;
    image: string | null;
    vendorId: number | null;
    categoryName: string | null;
    sizeChart: 'apparel' | 'lehenga' | 'free-size';
    variants: { id: number; name: string; stock: number }[];
  };
}

export default function AddToBag({ product }: Props) {
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const single = product.variants.length === 1 ? product.variants[0] : null;
  const [variantId, setVariantId] = useState<number | null>(single?.id ?? null);
  const [qty, setQty] = useState(1);
  const [chartOpen, setChartOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [needSize, setNeedSize] = useState(false);

  const variant = product.variants.find((v) => v.id === variantId) ?? null;
  const soldOut = product.variants.length > 0 && product.variants.every((v) => v.stock <= 0);
  const maxQty = Math.max(1, Math.min(variant?.stock ?? 10, 10));
  const saved = isInWishlist(product.id);

  async function add() {
    if (!variant) {
      setNeedSize(true);
      return;
    }
    if (!product.vendorId) {
      toast.error('This piece isn’t available to order yet.');
      return;
    }
    setAdding(true);
    try {
      await addItem({
        cart_item_id: 0,
        cart_id: 0,
        product_id: product.id,
        productId: product.id,
        vendorId: product.vendorId,
        quantity: qty,
        unit_price: product.price,
        // Not sent: the cart API's input filter rejects typographic characters (e.g. "—")
        // in configuration strings; the cart falls back to products.name for display.
        name: '',
        product_name: product.name,
        slug: product.slug,
        image: product.image ?? undefined,
        category_name: product.categoryName ?? undefined,
        configuration: {
          type: 'garment',
          variantId: variant.id,
          size: variant.name,
        },
      });
      toast.success('Added to your bag', {
        description: `${product.name} · ${variant.name}`,
        action: { label: 'View bag', onClick: () => (window.location.href = '/cart') },
      });
    } finally {
      setAdding(false);
    }
  }

  return (
    <div>
      {product.variants.length > 0 && !single && (
        <fieldset>
          <div className="flex items-center justify-between">
            <legend className="text-[11px] uppercase tracking-[0.18em] text-al-cream/70">
              Size {variant && <span className="text-al-gold">— {variant.name}</span>}
            </legend>
            <button type="button" onClick={() => setChartOpen(true)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-al-gold hover:underline">
              <RulerIcon size={15} /> Size guide
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {product.variants.map((v) => {
              const out = v.stock <= 0;
              const selected = v.id === variantId;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={out}
                  onClick={() => {
                    setVariantId(v.id);
                    setNeedSize(false);
                    setQty(1);
                  }}
                  aria-pressed={selected}
                  className={`relative min-w-[3.25rem] rounded-sm border px-3 py-2.5 text-sm transition-colors ${
                    selected
                      ? 'border-al-gold bg-al-gold/10 text-al-gold'
                      : out
                        ? 'cursor-not-allowed border-al-cream/10 text-al-cream/30 line-through'
                        : 'border-al-cream/20 text-al-cream hover:border-al-gold/70'
                  }`}
                >
                  {v.name}
                </button>
              );
            })}
          </div>
          {needSize && <p className="mt-2 text-sm text-al-rose" role="alert">Please select a size.</p>}
          {variant && variant.stock > 0 && variant.stock <= 3 && (
            <p className="mt-2 text-sm text-al-gold">Only {variant.stock} left in this size</p>
          )}
        </fieldset>
      )}

      {single && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.18em] text-al-cream/70">Size — <span className="text-al-gold">{single.name}</span></p>
          <button type="button" onClick={() => setChartOpen(true)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-al-gold hover:underline">
            <RulerIcon size={15} /> Size guide
          </button>
        </div>
      )}

      <div className="mt-7 flex items-stretch gap-3">
        <div className="flex items-center rounded-sm border border-al-cream/20" role="group" aria-label="Quantity">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-3 text-al-cream/70 hover:text-al-gold" aria-label="Decrease quantity">−</button>
          <span className="w-8 text-center tabular-nums" aria-live="polite">{qty}</span>
          <button type="button" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="px-3.5 py-3 text-al-cream/70 hover:text-al-gold" aria-label="Increase quantity">+</button>
        </div>
        <button type="button" onClick={add} disabled={adding || soldOut} className="al-btn-primary flex-1">
          {soldOut ? 'Sold out' : adding ? 'Adding…' : 'Add to bag'}
        </button>
        <button
          type="button"
          onClick={() =>
            toggleWishlist({
              product_id: product.id,
              name: product.name,
              slug: product.slug,
              image: product.image ?? undefined,
              base_price: product.price,
              category_name: product.categoryName ?? undefined,
            })
          }
          className={`flex w-12 items-center justify-center rounded-sm border transition-colors ${saved ? 'border-al-gold text-al-gold' : 'border-al-cream/20 text-al-cream hover:border-al-gold hover:text-al-gold'}`}
          aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
          aria-pressed={saved}
        >
          <HeartIcon size={20} filled={saved} />
        </button>
      </div>
      <Link href="/cart" className="mt-3 block text-center text-[11px] uppercase tracking-[0.18em] text-al-cream/50 hover:text-al-gold">
        View bag
      </Link>

      <SizeChart open={chartOpen} onClose={() => setChartOpen(false)} kind={product.sizeChart} />
    </div>
  );
}
